import React, { useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPersonaChipClass, tipoGestionArt, estadoCotizacionArt } from '../../../../../utils/styles';
import { useAuth } from '../../../../hooks/useAuth';

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../../common/SortableHeader';
import PaginatorFooter from '../../common/PaginatorFooter';
import ColumnSelector from '../../common/ColumnSelector';
import TableContainer from '../../common/TableContainer';
import Icons from '../../common/Icons';

// MODALES Y COMPONENTES RECONECTADOS
import FiltrosDinamicos from '../../../../ui/FiltrosDinamicos.jsx';
import DetalleCotizacionArt from '../../../CotizacionesArt/DetalleCotizacionArtOld.jsx';
import DetalleAumentoArt from '../../../CotizacionesArt/DetalleAumentoArtOld.jsx';
import FormularioCotizacionArt from '../../../../Formularios/FormularioCotizacionArt';

// CONTROLADOR DE NEGOCIO
import { useBloqueosArtTable } from './useBloqueosArtTable';

// --- HELPERS DE FECHAS ---
const formatDateLocal = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const adjustedDate = new Date(date.getTime() + userTimezoneOffset);
    return adjustedDate.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// --- FORMATO DE ESTADOS CON EMOJI ---
const formatEstadoCotizacion = (estado) => {
    if (!estado) return 'Cotizada';
    const norm = String(estado).trim();
    const mapping = {
        'Cotizada': '✅ Cotizada',
        'Solicitada': '📄 Solicitada',
        'Bloqueada': '🔒 Bloqueada',
        'No cotiza': '❌ No cotiza'
    };
    return mapping[norm] || norm;
};

// --- SEMAFORIZACIÓN DE BLOQUEOS ---
const getBlockageStatus = (fechaBloqueo) => {
    if (!fechaBloqueo) return { label: 'Sin Fecha', color: 'bg-slate-100 text-slate-500 border-slate-200' };
    
    const dateParts = fechaBloqueo.split('-');
    const blockDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
    blockDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = blockDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
        return {
            label: 'Vencido',
            daysLeft: diffDays,
            color: 'bg-red-50 text-red-600 border-red-200 font-bold'
        };
    } else if (diffDays <= 5) {
        return {
            label: `Vence en ${diffDays}d`,
            daysLeft: diffDays,
            color: 'bg-amber-50 text-amber-600 border-amber-300 animate-pulse font-black'
        };
    } else {
        return {
            label: `Activo (${diffDays}d)`,
            daysLeft: diffDays,
            color: 'bg-emerald-50 text-emerald-700 border-emerald-200 font-bold'
        };
    }
};

// --- FILA MEMOIZADA ---
const TableRow = memo(({ item, isSelected, isGrouped, columnVisibility, onSelect, onAction, isFullUser }) => {
    
    // ◄◄◄ RESOLUTOR EN CALIENTE DEL ESTADO ESPECÍFICO DE COTIZACIÓN
    const specificEstado = useMemo(() => {
        if (isGrouped) return 'Cotizada'; // Valor por defecto visual para agrupaciones de cots
        
        // Buscamos de forma síncrona en memoria la compañía correspondiente en el array de cotizaciones
        const found = item.cotizaciones?.find(c => c.compania === item.companiaBloqueo);
        return found?.estadoCotizacion || found?.estadoRow || found?.estado || item.estadoCotizacion || item.estadoRow || item.estado || 'Cotizada';
    }, [item, isGrouped]);

    const renderBloqueosAgrupados = () => {
        const bloqueos = item.cotizaciones?.filter(o => o.tieneFechaBloqueo || o.estadoRow === 'Bloqueada' || o.fechaBloqueo) || [];
        return (
            <div className="flex flex-wrap gap-1.5 justify-center py-0.5">
                {bloqueos.map((b, idx) => {
                    const status = getBlockageStatus(b.fechaBloqueo);
                    return (
                        <div key={idx} className={`flex items-center gap-2 px-2.5 py-1 rounded-lg border text-[10px] uppercase shadow-sm ${status.color}`}>
                            <span className="font-black whitespace-nowrap">{b.compania}</span>
                            <div className="flex items-center gap-1 opacity-80 whitespace-nowrap font-semibold">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <span>{b.fechaBloqueo ? formatDateLocal(b.fechaBloqueo) : 'S/F'}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <tr 
            onClick={() => onSelect(item.id)} 
            className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 
                ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'}`}
        >
            {columnVisibility.fechaCarga && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs text-slate-500 tabular-nums">{item.fechaCarga ? new Date(item.fechaCarga).toLocaleDateString('es-AR') : '-'}</td>}
            {columnVisibility.displayNombre && <td className="p-2.5 px-4 border-b border-r border-slate-100 text-sm font-bold text-slate-900 truncate uppercase text-left max-w-[280px]" title={item.displayNombre}>{item.displayNombre || 'SIN NOMBRE'}</td>}
            {columnVisibility.displayDoc && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center text-xs font-mono text-slate-500">{item.displayDoc}</td>}
            {columnVisibility.numeroCotizacion && <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center font-bold text-blue-700 text-xs font-mono">{item.numeroCotizacion || 'S/N'}</td>}
            
            {columnVisibility.tipoEntidad && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${item.tipoEntidad === 'CLIENTE' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-purple-50 text-purple-700 border-purple-100'}`}>
                        {item.tipoEntidad}
                    </span>
                </td>
            )}
            {columnVisibility.tipoGestion && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${tipoGestionArt(item.tipoGestion)}`}>
                        {item.tipoGestion || '-'}
                    </span>
                </td>
            )}

            {isGrouped ? (
                columnVisibility.proteccionComercial && (
                    <td className="p-2.5 px-4 border-b border-r border-slate-100 bg-red-50/20 text-center">
                        {renderBloqueosAgrupados()}
                    </td>
                )
            ) : (
                <>
                    {columnVisibility.companiaBloqueo && (
                        <td className="p-2.5 px-4 border-b border-r border-slate-100 text-center font-black text-xs text-red-700 bg-red-50/15 uppercase">
                            {item.companiaBloqueo}
                        </td>
                    )}
                    {columnVisibility.fechaBloqueo && (
                        <td className="p-2.5 px-4 border-b border-r border-slate-100 text-center">
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                                <span className="font-bold text-xs text-slate-600 font-mono tabular-nums whitespace-nowrap">
                                    {item.fechaBloqueo ? formatDateLocal(item.fechaBloqueo) : 'S/F'}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase border ${getBlockageStatus(item.fechaBloqueo).color} whitespace-nowrap`}>
                                    {getBlockageStatus(item.fechaBloqueo).label}
                                </span>
                            </div>
                        </td>
                    )}
                    {/* COLUMNA ADICIONAL CON MAPEO DE EMOJIS */}
                    {columnVisibility.estadoCotizacion && (
                        <td className="p-2.5 px-4 border-b border-r border-slate-100 text-center">
                            <span className={`px-2.5 py-1 text-[11px] font-bold uppercase ${(specificEstado)}`}>
                                {formatEstadoCotizacion(specificEstado)}
                            </span>
                        </td>
                    )}
                </>
            )}

            {columnVisibility.canal && <td className="p-2.5 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-500 uppercase truncate" title={item.canal}>{item.canal || '-'}</td>}
            {columnVisibility.canalNombre && <td className="p-2.5 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-700 uppercase truncate" title={item.canalNombre}>{item.canalNombre || '-'}</td>}
            {columnVisibility.subcanalNombre && <td className="p-2.5 px-4 border-b border-r border-slate-100 text-left text-[11px] font-bold text-slate-500 uppercase truncate" title={item.subcanalNombre}>{item.subcanalNombre || '-'}</td>}
            
            {columnVisibility.vendedor && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                     {item.vendedor ? <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit mx-auto ${getPersonaChipClass(item.vendedor)}`}><span className="text-xs font-bold">{item.vendedor}</span></div> : '-'}
                </td>
            )}

            {/* Acciones */}
            <td className="p-2.5 px-4 text-center sticky right-0 z-20 bg-white group-hover:bg-slate-50 border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)] w-24">
                <div className="flex justify-center items-center gap-1.5">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'VIEW'); }} className="p-1 text-slate-400 hover:text-blue-600 transition-all active:scale-90" title="Ver Detalle"><Icons.Eye /></button>
                    {isFullUser && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'EDIT'); }} className="p-1 text-slate-400 hover:text-orange-600 transition-all active:scale-90" title="Editar"><Icons.Edit /></button>
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const BloqueosArt = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const {
        view,
        setView,
        loading,
        needsHydration,
        clientsCache,
        selectedItem,
        selectedClient,
        isProspect,
        isGrouped,
        setIsGrouped,
        isFullUser,
        tableState,
        itemsTable,
        handleAction,
        handleSuccess,
        setSelectedItem
    } = useBloqueosArtTable();

    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange
    } = tableState;

    // Normalizador de visibilidad para inyectar por defecto el estado de la cotización
    const normalizedColumnVisibility = useMemo(() => {
        return {
            estadoCotizacion: true,
            ...columnVisibility
        };
    }, [columnVisibility]);

    // --- RECTOR DE ETIQUETAS DINÁMICAS ---
    const columnLabels = useMemo(() => ({
        fechaCarga: "Fecha Carga", displayNombre: "Cliente / Prospecto", displayDoc: "CUIT / DNI",
        numeroCotizacion: "N° Cotización", tipoEntidad: "Tipo", tipoGestion: "Gestión",
        ...(isGrouped ? {
            proteccionComercial: "Protección Comercial"
        } : {
            companiaBloqueo: "Compañía Bloqueada",
            fechaBloqueo: "Fecha Límite",
            estadoCotizacion: "Estado de Cotización"
        }),
        canal: "Canal / Org", canalNombre: "Nombre Canal", subcanalNombre: "Subcanal / Asociado", vendedor: "Responsable"
    }), [isGrouped]);

    const handleActionCallback = useCallback((item, type) => {
        handleAction(item, type, navigate);
    }, [handleAction, navigate]);

    // Configuración de Filtros Dinámicos
    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(c => c[key]).filter(Boolean))].sort().map(v => ({label: v, value: v}));
        
        // Extractor defensivo compatible con inconsistencias del estado de cotización en DB
        const getUniqueWithFallback = (key, fallbacks = [], data = itemsTable) => {
            const values = data.map(c => {
                // RESOLUCIÓN EN FILTRO DINÁMICO: Extrae el estado individual para que no se mezcle con el general
                if (!isGrouped) {
                    const found = c.cotizaciones?.find(x => x.compania === c.companiaBloqueo);
                    const specificEstado = found?.estadoCotizacion || found?.estadoRow || found?.estado;
                    if (specificEstado) return specificEstado;
                }
                
                if (c[key]) return c[key];
                for (const fb of fallbacks) {
                    if (c[fb]) return c[fb];
                }
                return null;
            }).filter(Boolean);
            
            // Retornamos las opciones mostrando el label con emoji pero con el value original limpio para match síncrono
            return [...new Set(values)].sort().map(v => ({ label: formatEstadoCotizacion(v), value: v }));
        };

        const itemsFilteredByCanal = filterValues.canal?.length > 0 ? itemsTable.filter(d => filterValues.canal.includes(d.canal)) : itemsTable;
        const itemsFilteredByNombre = filterValues.canalNombre?.length > 0 ? itemsFilteredByCanal.filter(d => filterValues.canalNombre.includes(d.canalNombre)) : itemsFilteredByCanal;

        return [
            { name: 'search', label: 'Buscador General', type: 'text', placeholder: 'Cliente, Cotización o CUIT...', className: 'w-80' },
            { name: 'fechaCarga', label: 'Fecha de Carga', type: 'date-range' },
            { name: 'tipoIngreso', label: 'Tipo de Ingreso', type: 'select', isMulti: true, options: [{label: 'CLIENTE', value: 'CLIENTE'}, {label: 'PROSPECTO', value: 'PROSPECTO'}] },
            { name: 'tipoGestion', label: 'Tipo de Gestión', type: 'select', isMulti: true, options: getUnique('tipoGestion') },
            // Inyectamos el filtro de estado condicionado a que no esté agrupado
            ...(!isGrouped ? [{ name: 'estadoCotizacion', label: 'Estado de Cotización', type: 'select', isMulti: true, options: getUniqueWithFallback('estadoCotizacion', ['estadoRow', 'estado']) }] : []),
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique('canal') },
            { name: 'canalNombre', label: 'Nombre del Canal', type: 'select', isMulti: true, options: getUnique('canalNombre', itemsFilteredByCanal) },
            { name: 'subcanalNombre', label: 'Subcanal', type: 'select', isMulti: true, options: getUnique('subcanalNombre', itemsFilteredByNombre) },
            { name: 'vendedor', label: 'Responsable', type: 'select', isMulti: true, options: getUnique('vendedor') },
        ];
    }, [itemsTable, filterValues, isGrouped]);

    if (loading || needsHydration) return (
        <div className="flex items-center justify-center h-screen bg-white text-slate-400 font-bold uppercase tracking-widest text-[10px] animate-pulse">
            Sincronizando bloqueos de ART...
        </div>
    );

    if (view === 'detail' && selectedItem) {
        return (
            <div className="h-screen w-full bg-white overflow-y-auto animate-fade-in text-left">
                {selectedItem.tipoGestion === 'Aumento de Tarifa' ? (
                    <DetalleAumentoArt cotizacion={selectedItem} onBack={() => { setSelectedItem(null); setView('list'); }} />
                ) : (
                    <DetalleCotizacionArt cotizacion={selectedItem} onBack={() => { setSelectedItem(null); setView('list'); }} />
                )}
            </div>
        );
    }

    if (view === 'edit' && selectedItem) {
        return (
            <div className="animate-fade-in p-10 bg-slate-50 min-h-screen text-left font-['Inter']">
                <div className="max-w-5xl mx-auto">
                    <button onClick={() => { setSelectedItem(null); setView('list'); }} className="mb-6 text-slate-400 hover:text-slate-800 flex items-center gap-2 text-xs font-bold transition-colors uppercase tracking-widest">← Volver al Tablero</button>
                    <FormularioCotizacionArt 
                        entityData={selectedClient} 
                        isProspect={isProspect} 
                        initialData={selectedItem} 
                        onSuccess={handleSuccess} 
                        onCancel={() => { setView('list'); setSelectedItem(null); }} 
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen w-full bg-white text-slate-900 overflow-hidden antialiased font-['Inter'] text-left">
            <div className="h-1 bg-red-600 w-full shrink-0"></div>
            
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button onClick={() => navigate('/CargaCotizacionArtPage')} className="p-3 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-2xl transition-all border border-transparent hover:border-red-100 shadow-sm"><Icons.ArrowLeft /></button>
                    <div className="flex items-center gap-4 text-left">
                        <div className="p-2.5 bg-red-50 text-red-600 rounded-xl border border-red-100 shadow-sm text-left"><Icons.Lock className="w-6 h-6 text-red-600" /></div>
                        <div className="text-left">
                            <h2 className="text-xl font-black text-slate-800 tracking-tight leading-tight uppercase italic text-left">Tablero de Bloqueos</h2>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mt-1 text-left">Control preventivo y fechas de protección comercial ART</p>
                        </div>
                    </div>
                </div>
                
                <div className="flex items-center gap-4 text-left">
                    <button
                        onClick={() => { setIsGrouped(!isGrouped); setCurrentPage(1); }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl border font-bold text-xs transition-all shadow-sm select-none active:scale-95 transform ${
                            isGrouped 
                                ? 'bg-orange-600 text-white border-orange-600 shadow-orange-100' 
                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                        }`}
                        title={isGrouped ? "Desagrupar en un registro individual por bloqueo" : "Agrupar bloqueos por Cotización general"}
                    >
                        <Icons.History className={`w-4 h-4 ${isGrouped ? 'text-white' : 'text-slate-400'}`} /> 
                        <span>{isGrouped ? 'Desagrupar Bloqueos' : 'Agrupar por Cotización'}</span>
                    </button>

                    <ColumnSelector 
                        columnVisibility={normalizedColumnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-2xl p-1 pr-5 shadow-inner text-left">
                        <div className="bg-red-600 text-white px-4 py-1.5 rounded-xl text-lg font-black shadow-md">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Registros</span>
                    </div>
                </div>
            </header>

            <FiltrosDinamicos 
                key="bloqueos-art-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            <main className="flex-grow overflow-hidden flex flex-col bg-white">
                <TableContainer minWidth="2200px">
                    <thead>
                        <tr className="bg-slate-900 text-white text-left">
                            <SortableHeader label="Fecha Carga" sortKey="fechaCarga" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Cliente / Prospecto" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-72 text-left" />
                            <SortableHeader label="CUIT / DNI" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="N° Cotización" sortKey="numeroCotizacion" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-32 text-center font-mono" />
                            <SortableHeader label="Tipo" sortKey="tipoEntidad" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Gestión" sortKey="tipoGestion" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-48 text-center" />
                            
                            {isGrouped ? (
                                <SortableHeader label="Protección Comercial" sortKey="proteccionComercial" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-80 text-center bg-slate-900 border-slate-700 shadow-sm" />
                            ) : (
                                <>
                                    <SortableHeader label="Compañía Bloqueada" sortKey="companiaBloqueo" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-44 text-center bg-red-950/20 text-red-200 border-slate-700" />
                                    <SortableHeader label="Fecha Límite" sortKey="fechaBloqueo" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-44 text-center font-mono" />
                                    <SortableHeader label="Estado de Cotización" sortKey="estadoCotizacion" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-52 text-center" />
                                </>
                            )}

                            <SortableHeader label="Canal / Org" sortKey="canal" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-32 text-left" />
                            <SortableHeader label="Nombre Canal" sortKey="canalNombre" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <SortableHeader label="Subcanal / Asociado" sortKey="subcanalNombre" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <SortableHeader label="Responsable" sortKey="vendedor" sortConfig={sortConfig} columnVisibility={normalizedColumnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <th className="w-24 p-4 sticky right-0 top-0 bg-slate-900 border-b border-slate-700 z-[85] uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-left">
                        {paginatedData.map((item) => (
                            <TableRow 
                                key={item.id} 
                                item={item} 
                                isSelected={selectedRowId === item.id} 
                                isGrouped={isGrouped}
                                columnVisibility={normalizedColumnVisibility}
                                onSelect={setSelectedRowId} 
                                onAction={handleActionCallback} 
                                isFullUser={isFullUser} 
                            />
                        ))}
                    </tbody>
                </TableContainer>

                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="bloqueos"
                    accentClass="text-red-700 bg-red-50 border-red-100"
                />
            </main>
        </div>
    );
};

export default BloqueosArt;